import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const HeaderWrap = styled.header`
  position: relative;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: none;
`;

export const TopStrip = styled.div`
  background: ${({ theme }) => theme.colors.nearBlack};
  color: ${({ theme }) => theme.colors.white};
  font-size: 0.78rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 0.5rem 0;

  > div {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  span {
    color: ${({ theme }) => theme.colors.goldLight};
    letter-spacing: 0.24em;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 0.7rem;
    padding: 0.4rem 0;
    > div { justify-content: center; }
  }
`;

export const Bar = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
`;

export const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #000;
  align-self: stretch;
  /* Extend the black area to the left edge of the viewport, past the centered Bar padding */
  margin: -1rem 0 -1rem calc(50% - 50vw);
  padding: 0.5rem 1.5rem 0.5rem calc(50vw - 50% + 1.5rem);

  .mark {
    width: 88px;
    height: 88px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.nearBlack};
    color: ${({ theme }) => theme.colors.gold};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: 900;
    font-size: 2.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid ${({ theme }) => theme.colors.gold};
  }

  .logo {
    width: auto;
    height: 96px;
    max-width: 360px;
    object-fit: contain;
    display: block;
  }

  .text {
    display: flex;
    flex-direction: column;
    line-height: 1;
  }

  .name {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: 700;
    color: ${({ theme }) => theme.colors.nearBlack};
    font-size: 1.25rem;
    letter-spacing: 0.02em;
  }

  .tag {
    font-size: 0.65rem;
    color: ${({ theme }) => theme.colors.goldDark};
    text-transform: uppercase;
    letter-spacing: 0.22em;
    margin-top: 4px;
  }
`;

export const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.white};
    flex-direction: column;
    padding: 1rem 1.5rem 1.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    gap: 0.25rem;
    z-index: 1000;
  }
`;

export const NavLinkItem = styled(Link)`
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme, $active }) => ($active ? theme.colors.goldDark : theme.colors.nearBlack)};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 1rem;
    right: 1rem;
    bottom: 0.35rem;
    height: 2px;
    background: ${({ theme }) => theme.colors.gold};
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transform-origin: left;
    transition: transform 0.25s ease;
  }

  &:hover::after {
    transform: scaleX(1);
  }
`;

export const DropdownWrap = styled.div`
  position: relative;
`;

export const DropdownBtn = styled.button`
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.nearBlack};
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  svg { font-size: 0.7rem; color: ${({ theme }) => theme.colors.goldDark}; }

  &:hover { color: ${({ theme }) => theme.colors.goldDark}; }
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 260px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-top: 3px solid ${({ theme }) => theme.colors.gold};
  box-shadow: ${({ theme }) => theme.shadows.cardHover};
  padding: 0.5rem 0;
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  z-index: 1000;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    position: static;
    transform: none;
    box-shadow: none;
    border: none;
    border-left: 2px solid ${({ theme }) => theme.colors.gold};
    margin-left: 1rem;
    padding: 0.25rem 0;
    min-width: auto;
  }
`;

export const DropdownLink = styled(Link)`
  display: block;
  padding: 0.65rem 1.25rem;
  font-size: 0.92rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-left: 3px solid transparent;

  &:hover {
    background: ${({ theme }) => theme.colors.goldSoft};
    color: ${({ theme }) => theme.colors.nearBlack};
    border-left-color: ${({ theme }) => theme.colors.gold};
  }
`;

export const ContactCta = styled(Link)`
  padding: 0.6rem 1.25rem;
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.nearBlack};
  border: 1px solid ${({ theme }) => theme.colors.nearBlack};
  transition: ${({ theme }) => theme.transitions.base};
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.gold};
    border-color: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.nearBlack};
  }
`;

export const BurgerBtn = styled.button`
  display: none;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.nearBlack};

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    display: inline-flex;
  }
`;
